import {
  User,
  CreateUser,
  UpdateUser,
  SubscriptionPlanOption,
} from '@/integrations/mysql/types';
import { getApiEndpoint } from '@/config/api';
import { apiJson, apiJsonArray } from '@/lib/api-client';

const simpleHash = async (password: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password + 'livestay-salt-2024');
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

const simpleVerify = async (
  password: string,
  hash: string,
): Promise<boolean> => {
  const hashedPassword = await simpleHash(password);
  return hashedPassword === hash;
};

export class UserService {
  static async getUserById(id: number): Promise<User | null> {
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('userId');
    const userType = localStorage.getItem('userType');
    if (
      token &&
      (userType === 'admin' || (userId && parseInt(userId, 10) === id))
    ) {
      const u = await apiJson<User | null>(`api/users/${id}`);
      if (u) return u;
    }
    return apiJson<User | null>(`api/users/public/${id}`);
  }

  static async getOwnerContactDetails(
    ownerId: number,
  ): Promise<Pick<User, 'phone' | 'email' | 'phone_ddi'> | null> {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    return apiJson<Pick<User, 'phone' | 'email' | 'phone_ddi'> | null>(
      `api/users/public/${ownerId}/contact`,
    );
  }

  static async getUserByEmail(_email: string): Promise<User | null> {
    return null;
  }

  static async createUser(
    userData: CreateUser & { password?: string; is_active?: boolean },
  ): Promise<User> {
    if (userData.user_type === 'admin') {
      throw new Error(
        'N?o ? permitido criar usu?rios administradores por quest?es de seguran?a',
      );
    }
    const pwd = userData.password || userData.password_hash;
    const res = await apiJson<{ user: User }>('api/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email: userData.email,
        password: pwd,
        name: userData.name,
        phone: userData.phone,
        phone_ddi: userData.phone_ddi,
        user_type: userData.user_type,
      }),
    });
    return res.user;
  }

  static async updateUser(id: number, userData: UpdateUser): Promise<User | null> {
    if (userData.user_type === 'admin') {
      throw new Error(
        'N?o ? permitido alterar usu?rios para administrador por quest?es de seguran?a',
      );
    }
    const currentUser = await this.getUserById(id);
    if (currentUser && currentUser.user_type === 'admin') {
      throw new Error(
        'N?o ? permitido alterar dados de administradores por quest?es de seguran?a',
      );
    }
    const token = localStorage.getItem('authToken');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const updatePayload: Record<string, unknown> = {};
    if (userData.name !== undefined) updatePayload.name = userData.name;
    if (userData.email !== undefined) updatePayload.email = userData.email;
    if (userData.phone !== undefined) updatePayload.phone = userData.phone;
    if (userData.user_type !== undefined) {
      updatePayload.user_type = userData.user_type;
    }
    if (userData.is_active !== undefined) {
      updatePayload.is_active = userData.is_active;
    }
    if (userData.phone_ddi !== undefined) {
      updatePayload.phone_ddi = userData.phone_ddi;
    }
    if (userData.plan_id !== undefined) {
      updatePayload.plan_id = userData.plan_id;
    }
    if (
      userData.password !== undefined &&
      userData.password !== null &&
      userData.password.trim() !== ''
    ) {
      updatePayload.password = userData.password.trim();
    }
    if (Object.keys(updatePayload).length === 0) {
      return await this.getUserById(id);
    }
    const response = await fetch(getApiEndpoint(`/api/users/${id}`), {
      method: 'PUT',
      headers,
      body: JSON.stringify(updatePayload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      let errorData: { error?: string };
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || 'Erro ao atualizar usu?rio' };
      }
      throw new Error(errorData.error || `Erro ao atualizar usu?rio: ${response.status}`);
    }
    return await this.getUserById(id);
  }

  static async verifyPassword(
    email: string,
    password: string,
  ): Promise<User | null> {
    const user = await this.getUserByEmail(email);
    if (!user) return null;
    const isValid = await simpleVerify(password, user.password_hash);
    return isValid ? user : null;
  }

  static async hashPassword(password: string): Promise<string> {
    return await simpleHash(password);
  }

  static async getAllUsers(limit: number = 50, offset: number = 0): Promise<User[]> {
    const all = await apiJsonArray<User>('api/users?includeInactive=false');
    if (limit > 0) return all.slice(offset, offset + limit);
    return all;
  }

  static async getAllUsersWithInactive(
    limit: number = 50,
    offset: number = 0,
  ): Promise<User[]> {
    const all = await apiJsonArray<User>('api/users?includeInactive=true');
    if (limit > 0) return all.slice(offset, offset + limit);
    return all;
  }

  static async getTotalUsersCount(includeInactive: boolean = true): Promise<number> {
    const r = await apiJson<{ total: number }>(
      `api/users/count?includeInactive=${includeInactive}`,
    );
    return r.total;
  }

  static async deleteUser(id: number): Promise<boolean> {
    const r = await apiJson<{ success: boolean }>(`api/users/${id}`, {
      method: 'DELETE',
    });
    return r.success;
  }

  static async toggleUserStatus(id: number, isActive: boolean): Promise<boolean> {
    const user = await this.getUserById(id);
    if (user && user.user_type === 'admin') {
      throw new Error(
        'N?o ? permitido alterar status de administradores por quest?es de seguran?a',
      );
    }
    await apiJson(`api/users/${id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    });
    return true;
  }

  static async emailExists(email: string): Promise<boolean> {
    const r = await apiJson<{ exists: boolean }>(
      `api/users/exists-email/${encodeURIComponent(email)}`,
    );
    return r.exists;
  }

  static async approveUser(id: number): Promise<User | null> {
    return apiJson<User | null>(`api/users/${id}/approve`, { method: 'PATCH' });
  }

  static async rejectUser(id: number): Promise<User | null> {
    return apiJson<User | null>(`api/users/${id}/reject`, { method: 'PATCH' });
  }

  static async listSubscriptionPlans(): Promise<SubscriptionPlanOption[]> {
    return apiJsonArray<SubscriptionPlanOption>('api/users/plans/list');
  }
}
